import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Siren, Clock3, CheckCircle2, BellOff, MessageCircle } from "lucide-react";

// V1.50 · Alerta SUPREMA (roja) al entrar: hitos del Ciclo DGT (matriz /
// franquicia / permiso ciclo_dgt) y recordatorio de examen para profesores.
// Acciones: Recordar más tarde (+1h), Entendido (solo esta sesión),
// No recordar de nuevo (permanente).
const SESSION_KEY = "supreme-alerts-session-acks";

const sessionAcks = () => {
  try { return JSON.parse(sessionStorage.getItem(SESSION_KEY) || "[]"); } catch (_) { return []; }
};
const addSessionAck = (key) => {
  const next = [...sessionAcks(), key];
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(next));
};

export const SupremeAlertDialog = () => {
  const [alerts, setAlerts] = useState([]);
  const [phones, setPhones] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/supreme-alerts");
      const acked = sessionAcks();
      setAlerts((data.alerts || []).filter((a) => !acked.includes(a.key)));
      setPhones(data.whatsapp_phones || []);
    } catch (_) { /* no autenticado aún */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const dismiss = (key) => setAlerts((list) => list.filter((a) => a.key !== key));

  const act = async (key, action) => {
    if (action === "ack") {
      addSessionAck(key);
      dismiss(key);
      return;
    }
    try { await api.post("/supreme-alerts/ack", { key, action }); } catch (_) { /* noop */ }
    dismiss(key);
  };

  if (alerts.length === 0) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4" data-testid="supreme-alert-overlay">
      <div className="w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-2xl border-4 border-red-600 bg-white shadow-2xl">
        <div className="flex items-center gap-2.5 px-5 py-3.5 bg-red-600 text-white sticky top-0">
          <Siren className="h-6 w-6 animate-pulse" />
          <div>
            <div className="text-sm font-black uppercase tracking-wider">Alerta de máxima prioridad</div>
            <div className="text-[11px] opacity-90">Requiere tu atención antes de continuar</div>
          </div>
        </div>
        <div className="divide-y divide-red-100">
          {alerts.map((a) => (
            <div key={a.key} className="p-5 space-y-3" data-testid={`supreme-alert-${a.key}`}>
              <div className="text-[10px] font-black uppercase tracking-widest text-red-600">{a.title}</div>
              <p className="text-sm font-semibold text-neutral-900 leading-relaxed">{a.message}</p>
              {a.kind === "ciclo" && phones.length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-[10px] font-bold uppercase text-neutral-500">Avisar por WhatsApp:</span>
                  {phones.map((p) => (
                    <a
                      key={p}
                      href={`https://wa.me/${p.replace("+", "")}?text=${encodeURIComponent(a.wa_message)}`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`supreme-wa-${a.key}-${p}`}
                      className="inline-flex items-center gap-1 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white px-2.5 py-1 text-[11px] font-bold"
                    >
                      <MessageCircle className="h-3 w-3" /> {p}
                    </a>
                  ))}
                </div>
              )}
              <div className="flex flex-wrap gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => act(a.key, "later")}
                  data-testid={`supreme-later-${a.key}`}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-neutral-300 bg-white hover:bg-neutral-50 text-neutral-700 px-3 h-9 text-xs font-semibold"
                >
                  <Clock3 className="h-3.5 w-3.5" /> Recordar más tarde
                </button>
                <button
                  type="button"
                  onClick={() => act(a.key, "ack")}
                  data-testid={`supreme-ack-${a.key}`}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 hover:bg-red-700 text-white px-3 h-9 text-xs font-bold"
                >
                  <CheckCircle2 className="h-3.5 w-3.5" /> Entendido, me encargo
                </button>
                <button
                  type="button"
                  onClick={() => act(a.key, "never")}
                  data-testid={`supreme-never-${a.key}`}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-neutral-200 bg-neutral-50 hover:bg-neutral-100 text-neutral-500 px-3 h-9 text-xs font-semibold"
                >
                  <BellOff className="h-3.5 w-3.5" /> No recordar de nuevo
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SupremeAlertDialog;
